"use client";

import { recordSignupActivity } from "@faire/retailer-visitor-shared/actions/user/recordLoggedOutActivity";
import { LazyDevTools } from "@faire/retailer-visitor-shared/components/DevTools/index";
import { signUpCache } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useFaireDirectInfo } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectInfo/useFaireDirectInfo";
import { isWidget } from "@faire/retailer-visitor-shared/lib/signUpRevampUtils";
import { getIsProduction } from "@faire/retailer-visitor-shared/serialized-data/getEnvName";
import { useAssignSettingLazyLoadSignupRoutes } from "@faire/retailer-visitor-shared/settings/useSettingLazyLoadSignupRoutes";
import { useAssignSettingSignupAppRouterMigration } from "@faire/retailer-visitor-shared/settings/useSettingSignupAppRouterMigration";
import {
  useAssignSettingSignUpSplit,
  useSettingSignUpSplit,
} from "@faire/retailer-visitor-shared/settings/useSettingSignUpSplit";
import { RedirectIfInvalid } from "@faire/retailer-visitor-shared/views/SignUp/_components/RedirectIfInvalid";
import { SignUpHeader } from "@faire/retailer-visitor-shared/views/SignUp/_components/SignUpHeader";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { useSignUpStepStoreValue } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpStepStoreValue";
import { VerticalizedSignUpLayout } from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/VerticalizedSignUpLayout";
import { SignUpRequestProvider } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { SignUpStepsProvider } from "@faire/retailer-visitor-shared/views/SignUp/SignUpStepsContext";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isE2E } from "@faire/web/common/user-agent/isE2E";
import { trackFaireDirectEnterFdWidgetSignupFlowView } from "@faire/web-api--source/events/faireDirect/view/enterFdWidgetSignupFlow";
import React, { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SignUpRoot: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const signUpStepsStoreValue = useSignUpStepStoreValue();
  const assignLazyLoadedRoutes = useAssignSettingLazyLoadSignupRoutes();
  const assignSignUpAppRouterMigrationSetting =
    useAssignSettingSignupAppRouterMigration();
  const assignSettingSignUpSplit = useAssignSettingSignUpSplit();
  useTrackEvents();
  useRecordExternalSignupLandingActivity();

  useEffect(() => {
    assignLazyLoadedRoutes();
    assignSignUpAppRouterMigrationSetting();
    assignSettingSignUpSplit();
  }, [
    assignLazyLoadedRoutes,
    assignSignUpAppRouterMigrationSetting,
    assignSettingSignUpSplit,
  ]);

  return (
    <SignUpRequestProvider value={signUpCache.get()}>
      <SignUpStepsProvider value={signUpStepsStoreValue}>
        <RedirectIfInvalid />
        <SignUpHeader />
        <VerticalizedSignUpLayout>{children}</VerticalizedSignUpLayout>
        <DevTools />
      </SignUpStepsProvider>
    </SignUpRequestProvider>
  );
};

const DevTools: React.FC = () => {
  const isSignUpSplitEnabled = useSettingSignUpSplit();
  const isSSR = useIsSSR();
  if (getIsProduction() || isE2E() || isSSR || isSignUpSplitEnabled) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <LazyDevTools app="retailer" />
    </Suspense>
  );
};

const useTrackEvents = () => {
  const { queryParams } = useSignUpQueryParams();

  useEffect(() => {
    trackEvent(`sign-up-page/view/${queryParams.signUp}`);
  }, [queryParams.signUp]);

  useEffect(() => {
    if (queryParams.widgetToken) {
      trackFaireDirectEnterFdWidgetSignupFlowView(queryParams.widgetToken);
    }
  }, [queryParams.widgetToken]);
};

const useRecordExternalSignupLandingActivity = () => {
  const { faireDirectBrandToken } = useFaireDirectInfo();
  const location = useLocation();

  useEffect(() => {
    if (!isWidget(location) || !faireDirectBrandToken) {
      return;
    }

    const loggedOutActivity = {
      brand_token: faireDirectBrandToken,
    };
    recordSignupActivity(loggedOutActivity, location.pathname);
  }, [faireDirectBrandToken, location, location.pathname]);
};
